import { environment } from './../../environments/environment';
import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  throwError,
  EMPTY,
  defer,
  firstValueFrom,
  from,
} from 'rxjs';
import { ApiService } from '../services/api.service';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { OnboardingService } from '../services/onboarding.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private refresh: Promise<string | null> | null = null;

  private apiService = inject(ApiService);
  private onboardingService = inject(OnboardingService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Check if we need additional token logic or not
    if (!this.shouldAddToken(request.url)) {
      return next.handle(request);
    }

    const token$ = this.refresh
      ? from(this.refresh).pipe(filter((token) => !!token))
      : from(this.apiService.getAccessToken());

    return token$.pipe(
      switchMap((token) => this.handleWithToken(request, next, token))
    );
  }

  // Filter out URLs where you don't want to add the token!
  private shouldAddToken(url: string): boolean {
    if (url === `${environment.baseURL}/auth/token`) {
      return false;
    }

    return url.startsWith(environment.baseURL);
  }

  private addToken(req: HttpRequest<unknown>, token: string | null) {
    if (token) {
      const headers = req.headers.set('Authorization', `Bearer ${token}`);
      return req.clone({ headers });
    }
    return req;
  }

  private getToken(): Promise<string | null> {
    return this.refresh ?? this.apiService.getAccessToken();
  }

  private handleWithToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    token: string | null,
    retried = false
  ): Observable<HttpEvent<unknown>> {
    return next.handle(this.addToken(request, token)).pipe(
      catchError((err) => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 401 &&
          !retried
        ) {
          return this.handle401Error(request, next, token, err);
        }

        return throwError(() => err);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    rejectedToken: string | null,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    return defer(async () => {
      // The token already changed since this request was sent
      const token = await this.getToken();
      if (token !== rejectedToken) {
        return token;
      }

      this.refresh ??= this.refreshToken().finally(() => {
        this.refresh = null;
      });
      return this.refresh;
    }).pipe(
      catchError(() => throwError(() => error)),
      switchMap((token) =>
        token ? this.handleWithToken(request, next, token, true) : EMPTY
      )
    );
  }

  private async refreshToken(): Promise<string | null> {
    try {
      const tokens = await firstValueFrom(this.apiService.getNewAccessToken());
      if (tokens) {
        await firstValueFrom(this.apiService.storeTokens(tokens));
        return tokens.access_token;
      }

      // Without a refresh token the rejected access token can never be renewed
      if (this.apiService.currentAccessToken) {
        await this.logout();
      }
      return null;
    } catch (error) {
      if (
        error instanceof HttpErrorResponse &&
        this.isRefreshTokenExpired(error)
      ) {
        await this.apiService.clearAuthenticationAfterInactivity();
        return null;
      }

      if (
        error instanceof HttpErrorResponse &&
        (error.error?.error === 'invalid_request' ||
          error.error?.error === 'invalid_grant')
      ) {
        // The refresh token itself is invalid/revoked, so we can no longer
        // authenticate to call the logout endpoint - just clear locally.
        await this.logout();
        return null;
      }

      throw error;
    }
  }

  private async logout(): Promise<void> {
    await this.apiService.clearAuthentication();
    this.onboardingService.showOnboarding();
  }

  // Refresh tokens rotate on every use, so an expired one means the app was not used for the
  // whole refresh token TTL. A revoked token gets a different hint.
  private isRefreshTokenExpired(error: HttpErrorResponse): boolean {
    return (
      error.error?.error === 'invalid_grant' &&
      error.error?.hint === 'Token has expired'
    );
  }
}
